import React, {useState, useEffect} from "react";
import {
    Box,
    Typography,
    Tooltip,
    LinearProgress,
    Paper,
    Card,
    CardContent,
    Grid,
    Icon,
    Divider,
    useTheme,
    alpha,
    ButtonBase,
    IconButton,
    CircularProgress
} from "@mui/material";
import {styled} from "@mui/material/styles";
import CryptoJS from "crypto-js";
import {KEY} from "../../dummyData";

// Icons
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import SecurityIcon from '@mui/icons-material/Security';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';

// Styled components
const StyledTooltip = styled(({className, ...props}) => (
    <Tooltip {...props} classes={{popper: className}}/>
))(({theme}) => ({
    [`& .MuiTooltip-tooltip`]: {
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        maxWidth: 300,
        fontSize: theme.typography.pxToRem(12),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[3],
        padding: theme.spacing(1.5),
    },
}));

// Styled progress bar component
const StyledLinearProgress = styled(LinearProgress)(({theme, value}) => {
    let color = theme.palette.error.main;
    if (value >= 70) {
        color = theme.palette.success.main;
    } else if (value >= 40) {
        color = theme.palette.warning.main;
    }

    return {
        height: 8,
        borderRadius: 4,
        backgroundColor: alpha(color, 0.15),
        '& .MuiLinearProgress-bar': {
            backgroundColor: color,
            borderRadius: 4,
        },
    };
});

// Progress bar with label
const ProgressWithLabel = ({value, showPercentage = false}) => {
    const theme = useTheme();
    const progressValue = Math.max(0, Math.min(100, value));

    let textColor = theme.palette.error.main;
    if (progressValue >= 70) {
        textColor = theme.palette.success.main;
    } else if (progressValue >= 40) {
        textColor = theme.palette.warning.main;
    }

    return (
        <Box sx={{width: '100%'}}>
            <Box sx={{display: 'flex', alignItems: 'center', mb: 1}}>
                <Box sx={{width: '100%', mr: 1}}>
                    <StyledLinearProgress variant="determinate" value={progressValue}/>
                </Box>
                {showPercentage && (
                    <Typography variant="body2" fontWeight="medium" color={textColor}>
                        {`${Math.round(progressValue)}%`}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

// Dashboard stat card component
const StatCard = ({title, value, subtitle, icon, color, trend, trendValue, tooltip, onClick}) => {
    const theme = useTheme();
    const CardWrapper = onClick ? ButtonBase : Box;

    const cardContent = (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                transition: 'transform 0.3s, box-shadow 0.3s',
                overflow: 'visible',
                '&:hover': onClick ? {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                } : {}
            }}
        >
            <CardContent sx={{p: 3, height: '100%'}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3}}>
                    <Box>
                        <Typography
                            variant="subtitle2"
                            color="text.secondary"
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 0.5
                            }}
                        >
                            {title}
                            {tooltip && (
                                <StyledTooltip title={tooltip} arrow>
                                    <IconButton size="small" sx={{ml: 0.5, p: 0}}>
                                        <InfoOutlinedIcon fontSize="small" color="action"/>
                                    </IconButton>
                                </StyledTooltip>
                            )}
                        </Typography>
                        <Typography variant="h5" fontWeight="bold" sx={{color: color || 'text.primary'}}>
                            {value}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            bgcolor: alpha(color || theme.palette.primary.main, 0.1),
                            p: 1,
                            borderRadius: 2,
                            color: color || theme.palette.primary.main,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {icon}
                    </Box>
                </Box>

                <Box sx={{display: 'flex', alignItems: 'center', mt: 'auto'}}>
                    {trend && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: trend === 'up' ? theme.palette.success.main : theme.palette.error.main,
                                mr: 1
                            }}
                        >
                            {trend === 'up' ?
                                <ArrowUpwardIcon fontSize="small"/> :
                                <ArrowDownwardIcon fontSize="small"/>
                            }
                            <Typography variant="body2" fontWeight="medium">
                                {trendValue}
                            </Typography>
                        </Box>
                    )}
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );

    if (onClick) {
        return (
            <ButtonBase
                sx={{
                    display: 'block',
                    textAlign: 'left',
                    width: '100%'
                }}
                onClick={onClick}
            >
                {cardContent}
            </ButtonBase>
        );
    }

    return cardContent;
};

// Safe decryption function
const safeDecrypt = (key) => {
    try {
        const encrypted = localStorage.getItem(key);
        if (!encrypted) return null;
        const decrypted = CryptoJS.AES.decrypt(encrypted, KEY).toString(CryptoJS.enc.Utf8);
        if (!decrypted) return null;
        JSON.parse(decrypted); // Validate JSON
        return decrypted;
    } catch (error) {
        console.error(`Failed to decrypt ${key}:`, error);
        return null;
    }
};



// Main Dashboard Component
export default function EnhancedFeaturedInfo(props) {
    const theme = useTheme();
    const [originalData, setOriginalData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Parse data from localStorage
    useEffect(() => {
        const decrypted = props.portfolioDetails || safeDecrypt("portfolioDetails");
        try {
            if (decrypted) {
                const parsed = JSON.parse(decrypted);

                setOriginalData(parsed);
            } else {
                setError("No portfolio data available");
            }
            setLoading(false);
        } catch (error) {
            console.error("Error parsing portfolio data:", error);
            setError("Failed to load portfolio data");
            setLoading(false);
        }
    }, [props.portfolioDetails]);

    // Format currency function
    const formatCurrency = (value) => {
        return value.toLocaleString("en-KE", {
            style: "currency",
            currency: "KES",
            maximumFractionDigits: 0
        });
    };

    if (loading) {
        return (
            <Box sx={{p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%'}}>
                <CircularProgress/>
            </Box>
        );
    }

    if (error || !originalData) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error" align="center">
                    {error || "Unable to load dashboard data"}
                </Typography>
            </Box>
        );
    }

    // Extract data maps based on your logs
    const taxData = new Map(Object.entries(originalData.find(([key]) => key === "tax")?.[1] || {}));
    const incomeData = new Map(Object.entries(originalData.find(([key]) => key === "receivedIncomeMap")?.[1] || {}));
    const expenseData = new Map(Object.entries(originalData.find(([key]) => key === "Expense Cost")?.[1] || {}));
    const depositData = new Map(Object.entries(originalData.find(([key]) => key === "deposit_held")?.[1] || {}));
    const occupancyData = originalData.find(([key]) => key === "occupancy")?.[1] || 0;
    const expectedIncomeData = new Map(Object.entries(originalData.find(([key]) => key === "expectedIncomeMap")?.[1] || {}));
    const propertyCountData = originalData.find(([key]) => key === "number_of_properties")?.[1] || 0;

    const unitCountData = originalData.find(([key]) => key === "number_of_units")?.[1] || 0;

    // Get the metrics
    const tax = taxData.get("Total tax") || 0;
    const income = incomeData.get("Total income") || 0;
    const incomeExpected = expectedIncomeData.get("Total income") || 0;
    const expenses = expenseData.get("Total expenses") || 0;
    const deposit = depositData.get("Total deposit") || 0;
    const occupancy = occupancyData || 0;
    const profit = income - expenses - tax;
    const collectionRate = incomeExpected > 0 ? (income / incomeExpected) * 100 : 0;
    const propertyCount = propertyCountData || 0;

    return (
        <Box sx={{pt: 2, pb: 2}}>
            {/* Top row - Key financial metrics - Now using 4 equal columns */}
            <Grid container spacing={3} sx={{mb: 2}}>
                {/* Income */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Total Income"
                        value={formatCurrency(income)}
                        /*subtitle={`${Math.round(collectionRate)}% of expected`}
                        trendValue={formatCurrency(incomeExpected - income) + " remaining"}*/
                        icon={<AttachMoneyIcon/>}
                        color={theme.palette.success.main}
                        tooltip="Total income collected across all properties"
                    />
                </Grid>

                {/* Expenses */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Total Expenses"
                        value={formatCurrency(expenses)}
                        /*subtitle="Across all properties"*/
                        subtitle=""
                        icon={<MoneyOffIcon/>}
                        color={theme.palette.error.main}
                        tooltip="Combined expenses for all properties including maintenance and operations"
                    />
                </Grid>

                {/* Net Profit */}
{/*
                <Grid item xs={12} sm={6} md={3}>
                    <StatCard
                        title="Net Profit"
                        value={formatCurrency(profit)}
                        subtitle="After expenses & tax"
                        icon={<AccountBalanceWalletIcon />}
                        color={profit >= 0 ? theme.palette.success.main : theme.palette.error.main}
                        trend={profit > 0 ? "up" : "down"}
                        trendValue={`${Math.abs((profit / income * 100).toFixed(1))}% margin`}
                        tooltip="Net profit calculation: Income - Expenses - Tax"
                    />
                </Grid>
*/}

                {/* Deposit */}
                <Grid item xs={12} sm={6} md={4}>
                    <StatCard
                        title="Security Deposit"
                        value={formatCurrency(deposit)}
                        subtitle="As of today"
                        icon={<SecurityIcon/>}
                        color={theme.palette.info.main}
                        tooltip="Total security deposits held for all properties"
                    />
                </Grid>
            </Grid>

            {/* Bottom row - Property metrics - Now using 3 equal columns */}
            <Grid container spacing={3}>
                {/* Collection Rate */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <CardContent sx={{p: 3}}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Collection Rate
                                </Typography>
                                <Box
                                    sx={{
                                        bgcolor: alpha(theme.palette.success.main, 0.1),
                                        p: 1,
                                        borderRadius: 2,
                                        color: theme.palette.success.main
                                    }}
                                >
                                    <TrendingUpIcon/>
                                </Box>
                            </Box>

                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 2}}>
                                <Typography variant="h5" fontWeight="bold">
                                    {Math.round(collectionRate)}%
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    of {formatCurrency(incomeExpected)}
                                </Typography>
                            </Box>

                            <ProgressWithLabel value={collectionRate}/>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                    pt: 1,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Collected:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" color="success.main">
                                    {formatCurrency(income)}
                                </Typography>
                            </Box>

                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                <Typography variant="body2" color="text.secondary">
                                    Outstanding:
                                </Typography>
                                <Typography variant="body2" fontWeight="medium" color="error.main">
                                    {formatCurrency(incomeExpected - income)}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Portfolio Overview */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <CardContent sx={{p: 3}}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Portfolio Overview
                                </Typography>
                                <Box
                                    sx={{
                                        bgcolor: alpha(theme.palette.warning.main, 0.1),
                                        p: 1,
                                        borderRadius: 2,
                                        color: theme.palette.warning.main
                                    }}
                                >
                                    <HomeWorkIcon/>
                                </Box>
                            </Box>

                            <Box sx={{mb: 3}}>
                                <Typography variant="h5" fontWeight="bold" sx={{mb: 0.5}}>
                                    {propertyCount} Properties
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {unitCountData} total units
                                </Typography>
                            </Box>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    pt: 2,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                }}
                            >
                               {/* <Box>
                                    <Typography variant="body2" color="text.secondary">
                                        Avg units/property
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold">
                                        {propertyCount > 0 ? Math.round(unitCountData / propertyCount) : 0}
                                    </Typography>
                                </Box>
                                <Box sx={{textAlign: 'right'}}>
                                    <Typography variant="body2" color="text.secondary">
                                        Occupied units
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" color="primary.main">
                                        {Math.round((occupancy / 100) * unitCountData)}
                                    </Typography>
                                </Box>*/}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Occupancy */}
                <Grid item xs={12} sm={6} md={4}>
                    <Card
                        elevation={0}
                        sx={{
                            height: '100%',
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`
                        }}
                    >
                        <CardContent sx={{p: 3}}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Occupancy Rate
                                </Typography>
                                <Box
                                    sx={{
                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                        p: 1,
                                        borderRadius: 2,
                                        color: theme.palette.primary.main
                                    }}
                                >
                                    <PeopleAltIcon/>
                                </Box>
                            </Box>

                            <Typography variant="h5" fontWeight="bold" sx={{mb: 2}}>
                                {Math.round(occupancy)}%
                            </Typography>

                            <ProgressWithLabel value={occupancy}/>

                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    mt: 2,
                                    pt: 1,
                                    borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`
                                }}
                            >
                                <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
                                    {Math.round((occupancy / 100) * unitCountData)} occupied out of {unitCountData} available units
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
}